import { defineStore, getActivePinia } from 'pinia';
import { getSelfProfile } from '@/api/profileApi';
import { emptySessionSelf } from '@/utils/sessionIdentity';
import { normalizeSelfProfile } from '@/utils/selfProfileForm';

/**
 * Optional explicit profile, else Pinia sessionSelf when active.
 * Utils (messagingSender, directivesSession, …) use this so callers need not
 * pass the store; pass `null` to force PHR-only fallback in tests.
 */
export function peekSessionSelfProfile(explicit) {
  if (explicit !== undefined) return explicit;
  try {
    const pinia = getActivePinia();
    if (pinia) return useSessionSelfStore(pinia).profile;
  } catch (e) {
    /* Pinia not ready */
  }
  return null;
}

/**
 * Logged-in account row from MySQL (GetSelfProfile).
 * Clinical PHR stays in Vuex login/getAllData (Postgres).
 */
export const useSessionSelfStore = defineStore('sessionSelf', {
  state: () => ({
    profile: emptySessionSelf(),
    loading: false,
    error: null,
    /** `${accountnumber}:${unique_ID}` after a successful load */
    loadedKey: '',
    /** Join concurrent login/resume callers (same pattern as privileges). */
    inflight: null,
  }),

  getters: {
    isLoaded: (state) => !!state.loadedKey,
    unique_ID: (state) => String((state.profile && state.profile.unique_ID) || '').trim(),
    accountnumber: (state) =>
      String((state.profile && state.profile.accountnumber) || '').trim(),
  },

  actions: {
    clear() {
      this.profile = emptySessionSelf();
      this.error = null;
      this.loadedKey = '';
      this.loading = false;
      this.inflight = null;
    },

    /** Apply a known-good profile (e.g. after UpdateSelfProfile). */
    setFromProfile(raw) {
      const profile = normalizeSelfProfile(raw);
      this.profile = profile;
      this.error = null;
      const acct = String(profile.accountnumber || '').trim();
      const uid = String(profile.unique_ID || '').trim();
      this.loadedKey = acct && uid ? `${acct}:${uid}` : '';
    },

    /**
     * Load MySQL self for the current session cookie.
     * Concurrent callers join the in-flight request.
     */
    async fetchForCurrentSession() {
      if (this.inflight) {
        await this.inflight;
        return;
      }
      const run = this.loadSelf();
      this.inflight = run;
      try {
        await run;
      } finally {
        if (this.inflight === run) this.inflight = null;
      }
    },

    async loadSelf() {
      this.loading = true;
      this.error = null;
      try {
        const r = await getSelfProfile();
        if (!r.ok || !r.profile) {
          this.error = r.message || r.status || 'Could not load self profile';
          // Keep prior profile if any; do not blank the app on a transient failure.
          return;
        }
        this.setFromProfile(r.profile);
      } catch (e) {
        this.error = (e && e.message) || 'Could not load self profile';
      } finally {
        this.loading = false;
      }
    },
  },
});
